import { TEST_ID_WATCHLIST } from "e2e/testId";
import { AlignJustify, CircleMinus, Pencil } from "lucide-react-native";
import React, { memo } from "react";

import ScaleDecorator from "@/components/molecules/ScaleDecorator";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";
import { ifWeb } from "@/utils/platform";

/**
 * Props for WatchlistItemView component
 */
interface Props {
  item: WatchlistWatchlistMetaFragment;
  onRemovePress: () => void;
  loading: boolean;
  canBeDeleted?: boolean;
  onEditPress: () => void;
  drag: () => void;
  isActive?: boolean;
  isDark: boolean;
  showDragHandle?: boolean;
}

/**
 * Pure view component for watchlist item with edit, delete, and drag actions
 * Displays watchlist name with action buttons for editing, deleting, and reordering
 * @param root0 - Component props
 * @param root0.item - Watchlist item data
 * @param root0.onRemovePress - Handler for delete button press
 * @param root0.loading - Whether item is in loading state
 * @param root0.canBeDeleted - Whether item can be deleted
 * @param root0.onEditPress - Handler for edit button press
 * @param root0.drag - Handler for drag gesture
 * @param root0.isActive - Whether item is being dragged
 * @param root0.isDark - Whether dark theme is active
 * @param root0.showDragHandle - Whether to show drag handle button
 */
const WatchlistItemView = ({
  item,
  onRemovePress,
  loading,
  canBeDeleted,
  onEditPress,
  drag,
  isActive,
  isDark,
  showDragHandle,
}: Props) => (
  <ScaleDecorator>
    <Stack direction="horizontal" className="grow">
      <Stack
        direction="horizontal"
        className="flex-1 items-center gap-3 px-4 py-2"
      >
        <Box className="w-6">
          {canBeDeleted && (
            <Button
              testID={TEST_ID_WATCHLIST.DELETE_ICON}
              disabled={loading}
              variant="link"
              onPress={onRemovePress}
              accessibilityLabel="Delete watchlist"
              icon={CircleMinus}
              iconTone="danger"
              iconSize={ifWeb<"md" | undefined>("md", undefined)}
            />
          )}
        </Box>
        <Box className="flex-1">
          <Text
            variant="unstyled"
            className={`${isDark ? "text-content-primary" : "text-content-secondary"} ${ifWeb("text-body", "font-medium text-title")}`}
          >
            {loading ? "Updating..." : item.name}
          </Text>
        </Box>
        <Box className="w-6">
          <Button
            testID={TEST_ID_WATCHLIST.EDIT_BUTTON}
            disabled={loading || isActive}
            variant="link"
            onPress={onEditPress}
            size={ifWeb<"md" | undefined>("md", undefined)}
            accessibilityLabel="Edit watchlist"
            icon={Pencil}
            iconTone="info"
          />
        </Box>
      </Stack>
      {showDragHandle && (
        <Box className="border-l border-surface-muted px-5 py-2 opacity-50">
          <Button
            disabled={loading || isActive || !canBeDeleted}
            variant="link"
            onLongPress={drag}
            size={ifWeb<"md" | undefined>("md", undefined)}
            accessibilityLabel="Drag to reorder watchlist"
            icon={AlignJustify}
            iconTone={!canBeDeleted ? "muted" : "info"}
          />
        </Box>
      )}
    </Stack>
  </ScaleDecorator>
);

WatchlistItemView.displayName = "WatchlistItemView";

export default memo(WatchlistItemView);
